import { useAuth } from '@/hooks/useAuth';
import { Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAdviserIdentity } from '@/hooks/useAdviserIdentity';

// AUDIENCE-SEPARATION (Phase 4 build-wall): this shared guard is imported by ALL THREE build
// entries (internal/adviser/public), so it MUST NOT name any proprietary-research symbol. The
// former `requiredSurface` branch (which imported `useSurfaceAccess` — the has_surface_access
// RPC — and redirected to `/partner/research-hub`) was extracted into <SurfaceGate>, an
// internal-only component composed inside the research route fragment. Internal research-hub
// routes now wrap their element as <ProtectedRoute><SurfaceGate surface="cio">…, preserving the
// exact auth→surface sequence while keeping the research tokens out of the adviser/public graphs.
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  requireAuth?: boolean;
  requiredTeam?: string;
  // Phase 3 (F4): opt a route into the external-adviser path. Defaults false, so on EVERY
  // existing route this is dead code — staff behaviour is entirely unchanged. When true, an
  // authenticated external adviser (isAdviser) is granted directly, bypassing the role/team
  // gates they'd fail by construction; a non-staff non-adviser is sent to /adviser.
  allowAdviser?: boolean;
}

export const ProtectedRoute = ({
  children,
  requiredRoles = [],
  requireAuth = true,
  requiredTeam,
  allowAdviser = false
}: ProtectedRouteProps) => {
  const { isAuthenticated, hasRole, loading, staffMember, user, session, forcePasswordChange } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const adviser = useAdviserIdentity();

  console.log('🛡️ ProtectedRoute: Evaluating access', { 
    path: location.pathname,
    isAuthenticated, 
    hasStaffMember: !!staffMember, 
    hasUser: !!user,
    hasSession: !!session,
    sessionExpiry: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'N/A',
    loading, 
    requiredRoles,
    userEmail: user?.email,
    staffRole: staffMember?.role_level 
  });

  useEffect(() => {
    if (!loading && requireAuth && !isAuthenticated) {
      console.warn('⚠️ ProtectedRoute: Auth required but user not authenticated');
      toast({
        title: "Authentication Required",
        description: "Please log in to access this page.",
        variant: "destructive"
      });
    }
    
    if (!loading && isAuthenticated && requiredRoles.length > 0 && !hasRole(requiredRoles)) {
      console.warn('⚠️ ProtectedRoute: User lacks required roles', { 
        requiredRoles, 
        userRole: staffMember?.role_level 
      });
      toast({
        title: "Access Denied", 
        description: "You don't have permission to access this page.",
        variant: "destructive"
      });
    }
  }, [loading, isAuthenticated, hasRole, requiredRoles, requireAuth, toast, staffMember]);

  if (loading) {
    console.log('⏳ ProtectedRoute: Still loading auth state...');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
          <p className="mt-2 text-xs text-muted-foreground">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    console.log('🚫 ProtectedRoute: Redirecting to login - not authenticated');
    return <Navigate to="/partner/login" state={{ from: location }} replace />;
  }

  // Force password change redirect (skip if already on reset-password page)
  if (isAuthenticated && forcePasswordChange && location.pathname !== '/reset-password') {
    console.log('🔑 ProtectedRoute: Redirecting to password change - admin-forced reset');
    return <Navigate to="/reset-password?forced=true" replace />;
  }

  // Phase 3 (F4): external-adviser path. Only ever engaged on routes that explicitly opt in
  // with allowAdviser (defaults false ⇒ dead code on every existing route). Staff fall through
  // to the normal gates below (they have a staffMember, so isAdviser is false by construction).
  if (allowAdviser) {
    // While adviser identity is resolving (claim read + optional adviser_links fallback), hold
    // on a quiet spinner rather than flashing content or redirecting prematurely.
    if (adviser.loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      );
    }
    // A confirmed external adviser is granted directly — they'd fail the role/team/surface gates
    // below by construction, so short-circuit past them.
    if (adviser.isAdviser) {
      console.log('✅ ProtectedRoute: Access granted via external-adviser path');
      return <>{children}</>;
    }
    // Authenticated, but neither staff nor a linked adviser: send them to the adviser entry.
    if (!staffMember) {
      console.log('🚫 ProtectedRoute: Non-adviser on adviser-only route → /adviser');
      return <Navigate to="/adviser" replace />;
    }
    // Otherwise (staff): fall through to the normal role/team/surface gates unchanged.
  }

  // Denials land on /access-denied (never on itself, which would loop) carrying what was blocked
  // and what it required, so the page can explain how to request the right permission.
  const denied = (state: { requiredRoles?: string[]; requiredTeam?: string }) => (
    <Navigate
      to="/access-denied"
      replace
      state={{ from: location.pathname, ...state }}
    />
  );

  if (requiredRoles.length > 0 && !hasRole(requiredRoles)) {
    console.log('🚫 ProtectedRoute: Redirecting to /access-denied - insufficient permissions');
    return denied({ requiredRoles });
  }

  if (requiredTeam && staffMember?.team !== requiredTeam && staffMember?.role_level !== 'superadmin' && staffMember?.role_level !== 'admin') {
    console.log('🚫 ProtectedRoute: Redirecting to /access-denied - not in required team:', requiredTeam);
    return denied({ requiredTeam });
  }

  console.log('✅ ProtectedRoute: Access granted, rendering protected content');
  return <>{children}</>;
};